/**
 * Main VybeTrader client facade
 */

import {
  VybeTraderClientStream,
  type VybeOrderClosePayload,
} from './VybeTraderClientStream';
import type { ConnectionCredentials, ConnectionStatus } from '../../shared/types';

export class VybeTraderClient {
  private static instance: VybeTraderClient | null = null;
  private stream: VybeTraderClientStream;

  private constructor() {
    this.stream = new VybeTraderClientStream();
  }

  public static getInstance(): VybeTraderClient {
    if (!VybeTraderClient.instance) {
      VybeTraderClient.instance = new VybeTraderClient();
    }
    return VybeTraderClient.instance;
  }

  public async connect(credentials: ConnectionCredentials): Promise<void> {
    try {
      await this.stream.connect(credentials);
    } catch (error) {
      throw error;
    }
  }

  public async disconnect(): Promise<void> {
    try {
      await this.stream.disconnect();
    } catch (error) {
      throw error;
    }
  }

  /**
   * Hard-recycle the hub after a suspect wake (sleep/wake freshness gate,
   * proposal 23): force-stop the possibly half-open socket and reconnect with
   * the same credentials, then refill state via the snapshot-resync.
   */
  public async recycle(): Promise<void> {
    try {
      await this.stream.recycle();
    } catch (error) {
      console.error('[VybeTraderClient] Failed to recycle connection:', error);
      throw error;
    }
  }

  /** Run the shared snapshot-resync (re-subscribe + OHLC backfill). */
  public resyncMarketData(): void {
    this.stream.resyncMarketData();
  }

  /** Count of stale/out-of-order ticks the monotonic gate has dropped. */
  public getDroppedStaleTickCount(): number {
    return this.stream.getDroppedStaleTickCount();
  }

  public getConnectionStatus(): ConnectionStatus {
    return this.stream.getConnectionStatus();
  }

  public isConnected(): boolean {
    return this.stream.isConnected();
  }

  public async send(method: string, data?: unknown): Promise<void> {
    try {
      await this.stream.send(method, data);
    } catch (error) {
      throw error;
    }
  }

  // Quote subscription methods
  public async subscribeToSymbol(currency: string): Promise<void> {
    try {
      await this.stream.subscribeToSymbol(currency);
    } catch (error) {
      console.error('[VybeTraderClient] Failed to subscribe to symbol:', currency, error);
      throw error;
    }
  }

  public async unsubscribeFromSymbol(currency: string): Promise<void> {
    try {
      await this.stream.unsubscribeFromSymbol(currency);
    } catch (error) {
      console.error('[VybeTraderClient] Failed to unsubscribe from symbol:', currency, error);
      throw error;
    }
  }

  // Market depth subscription methods
  public async subscribeMarketDepth(symbol: string): Promise<void> {
    try {
      await this.stream.subscribeMarketDepth(symbol);
    } catch (error) {
      console.error('[VybeTraderClient] Failed to subscribe market depth:', symbol, error);
      throw error;
    }
  }

  public async unsubscribeMarketDepth(symbol: string): Promise<void> {
    try {
      await this.stream.unsubscribeMarketDepth(symbol);
    } catch (error) {
      console.error('[VybeTraderClient] Failed to unsubscribe market depth:', symbol, error);
      throw error;
    }
  }

  // Trade history methods
  public async requestAdditionalTradeHistory(fromDate: string, toDate: string): Promise<void> {
    try {
      await this.stream.requestAdditionalTradeHistory(fromDate, toDate);
    } catch (error) {
      console.error('[VybeTraderClient] Failed to request additional trade history:', error);
      throw error;
    }
  }

  /** Full closed + pending history for the default 1y window (server-reload / authoritative). */
  public async requestFullTradeHistoryReload(): Promise<void> {
    try {
      await this.stream.requestFullTradeHistoryReload();
    } catch (error) {
      console.error('[VybeTraderClient] Failed to reload trade history:', error);
      throw error;
    }
  }

  /** Re-request open trades from the server (`GetOpenTrades` hub method). */
  public async requestOpenTradesRefresh(): Promise<void> {
    try {
      await this.stream.send('GetOpenTrades');
    } catch (error) {
      console.error('[VybeTraderClient] Failed to refresh open trades:', error);
      throw error;
    }
  }

  // Trading operations
  public async sendOrder(orderData: unknown): Promise<void> {
    try {
      await this.stream.sendOrder(orderData);
    } catch (error) {
      console.error('[VybeTraderClient] Failed to send order:', error);
      throw error;
    }
  }

  public async modifyOrder(modifyData: unknown): Promise<void> {
    try {
      await this.stream.modifyOrder(modifyData);
    } catch (error) {
      console.error('[VybeTraderClient] Failed to modify order:', error);
      throw error;
    }
  }

  public async closeOrder(closeData: VybeOrderClosePayload): Promise<void> {
    try {
      await this.stream.closeOrder(closeData);
    } catch (error) {
      console.error('[VybeTraderClient] Failed to close order:', error);
      throw error;
    }
  }

  public async closeOrderByHedge(
    closeData: VybeOrderClosePayload & { CloseByTicket: number }
  ): Promise<void> {
    try {
      await this.stream.closeOrderByHedge(closeData);
    } catch (error) {
      console.error('[VybeTraderClient] Failed to close order by hedge:', error);
      throw error;
    }
  }

  public async sendMailboxMessage(payload: {
    to: string;
    subject: string;
    body: string;
    severity?: number;
    topicId?: string;
  }): Promise<void> {
    await this.stream.sendMailboxMessage(payload);
  }

  public async markMailboxMessageRead(messageId: string): Promise<void> {
    await this.stream.markMailboxMessageRead(messageId);
  }

  public async syncMailbox(request: {
    isFirstLoad: boolean;
    lastMsgSequence: number;
    missingMsgSequences: number[];
  }): Promise<void> {
    await this.stream.syncMailbox(request);
  }

  public async syncNews(request: {
    isFirstLoad: boolean;
    lastSequence: number;
    missingSequences: number[];
  }): Promise<void> {
    await this.stream.syncNews(request);
  }

  public async syncEconomicCalendar(request: {
    calendarYears: number[];
    monthVersions: Array<{ monthKey: string; versionHash: string }>;
    isFirstLoad?: boolean;
    lastSequence?: number;
    missingSequences?: number[];
  }): Promise<void> {
    await this.stream.syncEconomicCalendar(request);
  }

  public async syncEconomicCalendarDay(request: {
    dayKey: string;
  }): Promise<void> {
    await this.stream.syncEconomicCalendarDay(request);
  }

  public async syncEconomicCalendarMonth(request: {
    monthKey: string;
  }): Promise<void> {
    await this.stream.syncEconomicCalendarMonth(request);
  }

  /** DNS-resolve + TCP-ping a broker endpoint through the connected gateway. */
  public async resolveEndpoint(
    host: string,
    port: number
  ): Promise<{ success: boolean; ip?: string; port: number; latencyMs: number; errorMessage?: string }> {
    return this.stream.resolveEndpoint(host, port);
  }

  // Internal access to stream for advanced operations
  public getStream(): VybeTraderClientStream {
    return this.stream;
  }
}

/**
 * Bulk-close verbs shared by the desktop blotter menu/toolbar and the mobile
 * bulk-operations sheet so the two surfaces cannot drift.
 *
 *  - `all`        → every open position
 *  - `profitable` → positions with live `profit > 0`
 *  - `losing`     → positions with live `profit < 0`
 */
export type BulkCloseMode = 'all' | 'profitable' | 'losing';

/** Minimal shape the bulk-close selector needs from a live open position. */
export interface BulkClosablePosition {
  ticket: number;
  profit: number;
}

export interface BulkCloseSelection<T extends BulkClosablePosition> {
  /** The positions the verb would close, in input order. */
  positions: T[];
  /** Their tickets, ready to fan out through the close path. */
  tickets: number[];
  /** Sum of live `profit` across the selection (est. realized P&L). */
  realizedPnl: number;
}

/**
 * Pure selector: given the live open-position set and a mode, returns the
 * positions/tickets that verb would close plus their summed live P&L. No side
 * effects — the caller is responsible for the count-confirm + close fan-out.
 */
export function selectBulkClose<T extends BulkClosablePosition>(
  positions: readonly T[],
  mode: BulkCloseMode
): BulkCloseSelection<T> {
  const selected = positions.filter((p) => {
    if (mode === 'profitable') return p.profit > 0;
    if (mode === 'losing') return p.profit < 0;
    return true; // 'all'
  });

  return {
    positions: selected,
    tickets: selected.map((p) => p.ticket),
    realizedPnl: selected.reduce((sum, p) => sum + p.profit, 0),
  };
}
